import React, { ReactElement, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { BoardContext, useBoardStateSelector } from 'store/contexts/board';
import { useContextSelector } from 'use-context-selector';
import { generateEmptyGrid } from 'components/Board/utils';
import Slider from 'components/Slider';

import { getAdvancedGrid } from './lifeAgent';

const ControlPanel = (): ReactElement => {
  const boardState = useBoardStateSelector();
  const setBoardState = useContextSelector(
    BoardContext,
    context => context.setState,
  );

  const resetBoard = useCallback(() => {
    setBoardState(state => ({
      ...state,
      grid: generateEmptyGrid(boardState.numOfRows, boardState.numOfCols),
    }));
  }, [boardState.numOfCols, boardState.numOfRows, setBoardState]);

  const toggleEditMode = useCallback((): void => {
    setBoardState(state => ({
      ...state,
      isEditingGrid: !boardState.isEditingGrid,
    }));
  }, [boardState.isEditingGrid, setBoardState]);

  const updateDimension = useCallback(
    ({ nRow, nCol }: { nRow?: number; nCol?: number }): void => {
      setBoardState(state => ({
        ...state,
        numOfRows: nRow || boardState.numOfRows,
        numOfCols: nCol || boardState.numOfCols,
      }));
    },
    [boardState.numOfCols, boardState.numOfRows, setBoardState],
  );

  const advance = useCallback((): void => {
    setBoardState(state => ({
      ...state,
      grid: getAdvancedGrid(boardState.grid),
    }));
  }, [boardState.grid, setBoardState]);

  const toggleRunGame = useCallback((): void => {
    setBoardState(state => ({
      ...state,
      isRunning: !boardState.isRunning,
    }));
  }, [boardState.isRunning, setBoardState]);

  useEffect(() => {
    if (boardState.isRunning && boardState.iterationDone) {
      setBoardState(state => ({ ...state, iterationDone: false }));
      advance();
      setTimeout(() => {
        setBoardState(state => ({ ...state, iterationDone: true }));
      }, 75);
    }
  }, [advance, boardState.isRunning, boardState.iterationDone, setBoardState]);

  return (
    <>
      <Panel>
        <PanelButton onClick={(): void => toggleEditMode()}>
          {boardState.isEditingGrid ? 'Edit Mode' : 'Watch Mode'}
        </PanelButton>
        <PanelButton onClick={resetBoard}>Reset Board</PanelButton>
        <PanelButton onClick={toggleRunGame}>
          {boardState.isRunning ? 'Stop' : 'Start'}
        </PanelButton>
        <PanelButton onClick={advance}>Advance 1 Generation</PanelButton>
        <Slider
          header="Number of Rows"
          max={100}
          value={boardState.numOfRows}
          onChange={(val: number): void => updateDimension({ nRow: val })}
        />
        <Slider
          header="Number of Cols"
          max={100}
          value={boardState.numOfCols}
          onChange={(val: number): void => updateDimension({ nCol: val })}
        />
      </Panel>
    </>
  );
};

const Panel = styled.div`
  margin: 0em;
  margin-right: -0.25em;
  padding: 1em 1em 2em 0;
  display: flex;
  flex-direction: row;
  position: sticky;
  top: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 1),
    rgba(255, 255, 255, 0.9)
  );
`;

const PanelButton = styled.button`
  background-color: lavender;
  border: 1px solid black;
  border-radius: 0.2rem;
  padding: 0.7em;
  margin: 0 5px 0 0;
  cursor: pointer;
  min-width: 8em;
`;

export default ControlPanel;
